<template>
  <div class="modify-room-overview">
    <div class="modify-room-overview__date">
      <div class="modify-room-overview__date-label">
        {{ ModifyReservationDateTitle }}
      </div>
      <div class="modify-room-overview__date-entry">
        <div class="modify-room-overview__day">{{ dayIn }}</div>
        <div class="modify-room-overview__date-info">{{ startDate }}</div>
      </div>
      <div class="modify-room-overview__date-entry">
        <div class="modify-room-overview__day">{{ dayOut }}</div>
        <div class="modify-room-overview__date-info">{{ endDate }}</div>
      </div>
    </div>
    <div class="modify-room-overview__date-arrow">→</div>
    <div class="modify-room-overview__hours">
      <div class="modify-room-overview__check-in">
        <div class="modify-room-overview__label">
          {{ ModifyReservationCheckInTitle }}
        </div>
        <div class="modify-room-overview__time">
          {{ tc("booking.after") }} {{ timeIn12hFormat(checkInTime) }}
        </div>
      </div>
      <div class="modify-room-overview__check-out">
        <div class="modify-room-overview__label">
          {{ ModifyReservationCheckOutTitle }}
        </div>
        <div class="modify-room-overview__time">
          {{ tc("booking.before") }} {{ timeIn12hFormat(checkOutTime) }}
        </div>
      </div>
    </div>
    <div :class="`modify-room-overview__room ${!isMultiRoomReservation ? 'single' : 'multi-room'}`">
      <div class="modify-room-overview__label">
        <span v-if="!isMultiRoomReservation">{{ ModifyReservationRoomTitle }}</span>
        <span v-if="isMultiRoomReservation">{{ ModifyReservationRoomsTitle }}</span>
      </div>
      <div
        v-for="reservation in reservations"
        :key="reservation.id"
        class="modify-room-overview__type"
      >
        {{ ModifyReservationRoomTitle }} {{ reservation.roomNumberOrder }}:
        {{ reservation.roomStays[0].roomName }}
      </div>
    </div>
    <div class="modify-room-overview__policies">
      <toggle-details>
        <div slot-scope="{ opened, toggle, isMounted }">
          <div class="detailed-content" :class="[{ opened }]">
            <div class="activator">
              <div
                role="button"
                tabindex="0"
                class="modify-room-overview__policies-label"
                v-on:click="toggle"
                @keydown.enter.prevent="toggle"
                @keydown.space.prevent="toggle"
                v-bind:aria-expanded="opened ? 'true' : 'false'"
              >
                <div class="modify-room-overview__policies-block">
                  <div class="modify-room-overview__policies-title">
                    {{ ModifyReservationPoliciesTitle }}
                  </div>
                  <div :class="opened ? 'icon-minus' : 'icon-add'"></div>
                </div>
              </div>
            </div>
            <div
              class="modify-room-overview__policies-list"
              :class="[{ opened: opened }]"
            >
              <transition-group name="expand-y">
                <div v-if="opened" key="modify-room-overview__policies-list">
                  <div class="modify-room-overview__policies-label">
                    {{ ModifyReservationGuaranteePolicyTitle }}
                  </div>
                  <!-- html is off from synxis-->
                  <ul
                    v-html="guaranteePolicy"
                    :class="`modify-room-overview__policies-content ${indentGuaranteePolicyHtmMarkup}`"
                  ></ul>
                </div>
              </transition-group>
            </div>
          </div>
        </div>
      </toggle-details>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { formatDateZone, timeIn12h } from "@utils/date-helpers";

export default {
  name: "modify-room-overview",
  props: {
    reservations: {
      type: Array,
      required: true
    }
  },
  computed: {
    ...mapState("modifyReservation", [
      "currentReservationData",
      "modifyBookingPropData",
    ]),
    ...mapGetters("i18n", ["tc"]),
    ...mapGetters(
      "modifyReservation", ["isMultiRoomReservation"]
    ),
    dayIn() {
      return formatDateZone(
        this.reservations[0].roomStays[0].startDate,
        null,
        "dddd"
      );
    },
    dayOut() {
      return formatDateZone(
        this.reservations[0].roomStays[0].endDate,
        null,
        "dddd"
      );
    },
    startDate() {
      return formatDateZone(
        this.reservations[0].roomStays[0].startDate,
        null,
        "D MMM, YYYY"
      );
    },
    endDate() {
      return formatDateZone(
        this.reservations[0].roomStays[0].endDate,
        null,
        "D MMM, YYYY"
      );
    },
    checkInTime() {
      return this.currentReservationData?.property.checkIn
        ? this.currentReservationData?.property.checkIn
        : "15:00";
    },
    checkOutTime() {
      return this.currentReservationData?.property.checkOut
        ? this.currentReservationData?.property.checkOut
        : "12:00";
    },
    guaranteePolicy() {
      return this.reservations[0].roomStays[0]
        .ratePlans[0].guarantees[0];
    },
    indentGuaranteePolicyHtmMarkup() {
      const markup = this.reservations[0].roomStays[0].ratePlans[0].guarantees[0];
      const markupWithHtml = /<li>/.test(markup)
      return markupWithHtml ? 'list-indent' : ''
    },
    ModifyReservationDateReservationTitle() {
      return this.modifyBookingPropData?.ModifyReservationDateReservationTitle
        ? this.modifyBookingPropData.ModifyReservationDateReservationTitle
        : "";
    },
    ModifyReservationCheckInTitle() {
      return this.modifyBookingPropData?.ModifyReservationCheckInTitle
        ? this.modifyBookingPropData.ModifyReservationCheckInTitle
        : "";
    },
    ModifyReservationCheckOutTitle() {
      return this.modifyBookingPropData?.ModifyReservationCheckOutTitle
        ? this.modifyBookingPropData.ModifyReservationCheckOutTitle
        : "";
    },
    ModifyReservationRoomTitle() {
      return this.modifyBookingPropData?.ModifyReservationRoomTitle
        ? this.modifyBookingPropData.ModifyReservationRoomTitle
        : "";
    },
    ModifyReservationRoomsTitle() {
      return this.modifyBookingPropData?.ModifyReservationRoomsTitle
        ? this.modifyBookingPropData.ModifyReservationRoomsTitle
        : "";
    },
    ModifyReservationPoliciesTitle() {
      return this.modifyBookingPropData?.ModifyReservationPoliciesTitle
        ? this.modifyBookingPropData.ModifyReservationPoliciesTitle
        : "";
    },
    ModifyReservationGuaranteePolicyTitle() {
      return this.modifyBookingPropData?.ModifyReservationGuaranteePolicyTitle
        ? this.modifyBookingPropData.ModifyReservationGuaranteePolicyTitle
        : "";
    },
    ModifyReservationDateTitle() {
      return this.modifyBookingPropData?.ModifyReservationDateTitle
        ? this.modifyBookingPropData.ModifyReservationDateTitle
        : "";
    },
  },
  mounted() {},
  methods: {
    timeIn12hFormat(time) {
       return timeIn12h(time)
    },
  },
};
</script>
